import { useState, useEffect } from "react";
import axios from "axios";
import { useMutation } from "@apollo/client";
import { Image } from "primereact/image";
import GlobalDialog from "../../../../components/core/global-dialog";
import GlobalButton from "../../../../components/core/global-button";
import Modal from "../../../../components/core/modal";
import ConfirmationDialog from "./confirmation-dialog";
import {
  UPDATE_AGENT,
  SAVE_STATUS_HISTORY,
} from "../../../../constants/graphql/partner-agent-queries";
import { Strings } from "../../../../constants/strings";

const PHOTO_INDEX_TO_LABEL = {
  0: "Agent Photo / Selfie", // submission_type 1, 2
  1: "Courier Company ID (Front)", // submission_type 1
  2: "Courier Company ID (Back)", // submission_type 1
  3: "Agent Selfie with ID", // submission_type 1, 2
  4: "Government ID (Front)", // submission_type 2
  5: "Courier Company App Screenshot", // submission_type 2
  101: "Mismatch Name",
  102: "Mismatch ID Number",
  103: "Mismatch Partner",
};

const SUBMISSION_TYPE_PHOTO_INDICES = {
  1: [0, 1, 2, 3],
  2: [0, 3, 4, 5],
};

const FIELD_INDICES = [6, 7, 8];

const SUBMISSION_LABELS = {
  "Agent Photo / Selfie": "your Selfie",
  "Courier Company ID (Front)": "a valid Courier Company ID (Front)",
  "Courier Company ID (Back)": "a valid Courier Company ID (Back)",
  "Agent Selfie with ID": "your Selfie with ID",
  "Government ID (Front)": "a valid Government ID (Front)",
  "Courier Company App Screenshot": "a Courier Company App Screenshot",
  "Mismatch Name": "a correct Name",
  "Mismatch ID Number": "a correct ID Number",
  "Mismatch Partner": "a correct Partner",
};

const DEFAULT_REJECTION_MESSAGE =
  "Your MBOX Courier registration is rejected due to missing / invalid requirement submitted. To reapply, kindly resubmit all required documents.";

const buildRejectionFallbackMessage = (rejectedIndices) => {
  if (!rejectedIndices?.length) return DEFAULT_REJECTION_MESSAGE;
  const labels = rejectedIndices
    .map((i) => SUBMISSION_LABELS[PHOTO_INDEX_TO_LABEL[i]])
    .filter(Boolean);
  if (!labels.length) return DEFAULT_REJECTION_MESSAGE;
  if (labels.length === 1) {
    return `You're just one step away from being a verified MBOX Courier! To complete your MBOX registration, please provide us with ${labels[0]}.`;
  }
  const copy = [...labels];
  const last = copy.pop();
  return `You're just one step away from being a verified MBOX Courier! To complete your MBOX registration, please provide us with ${copy.join(", ")} and ${last}.`;
};

const getOperatorId = () => {
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user).operator_id : "";
};

// Map alt text → backend index (single source of truth from PHOTO_INDEX_TO_LABEL)
const altToBackendIndex = Object.entries(PHOTO_INDEX_TO_LABEL).reduce(
  (acc, [idx, label]) => ({ ...acc, [label]: Number(idx) }),
  {},
);

const MAX_REJECTED_PHOTOS = 1;

export default function VerifyIdModal({
  open,
  onHide,
  submittedIds = [],
  agentData = {},
  partnerAgentId,
  submissionType = null,
  onSuccess,
}) {
  const [rejectedPhotos, setRejectedPhotos] = useState([]);
  const [rejectedFields, setRejectedFields] = useState([]);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null);
  const [validation, setValidation] = useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  const allRejected = [...rejectedPhotos, ...rejectedFields];
  const isMaxReached = allRejected.length >= 1;
  const isMaxFieldsReached = allRejected.length >= 1;

  const [updateAgent] = useMutation(UPDATE_AGENT);
  const [saveStatusHistory] = useMutation(SAVE_STATUS_HISTORY);

  // Reset rejected photos/fields when modal opens
  useEffect(() => {
    if (open) {
      setRejectedPhotos([]);
      setRejectedFields([]);
    }
  }, [open]);

  const toggleRejectedPhoto = (backendIndex) => {
    setRejectedPhotos((prev) => {
      if (prev.includes(backendIndex))
        return prev.filter((i) => i !== backendIndex);
      if (prev.length + rejectedFields.length >= 1) return prev;
      return [...prev, backendIndex];
    });
  };

  const toggleRejectedField = (backendIndex) => {
    setRejectedFields((prev) => {
      if (prev.includes(backendIndex))
        return prev.filter((i) => i !== backendIndex);
      if (rejectedPhotos.length + prev.length >= 1) return prev;
      return [...prev, backendIndex];
    });
  };

  const showSuccess = (message) => {
    setValidation({
      icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
      title: Strings.popup_success_title_default,
      message,
      open: true,
      actions: (
        <div className="w-full">
          <GlobalButton
            label="OK"
            buttonClassName="uppercase p-2 text-secondary-purpleGray w-full bg-primary-primaryYellow text-center border-0 hover:bg-primary-primaryYellowDark"
            onClick={() => {
              setValidation((prev) => ({ ...prev, open: false }));
              onHide?.();
              onSuccess?.();
            }}
          />
        </div>
      ),
    });
  };

  const showError = (message) => {
    setValidation({
      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
      title: Strings.popup_error_title,
      message,
      open: true,
      actions: (
        <div className="w-full">
          <GlobalButton
            label="OK"
            buttonClassName="uppercase p-2 text-secondary-purpleGray w-full bg-primary-primaryYellow text-center border-0 hover:bg-primary-primaryYellowDark"
            onClick={() => setValidation((prev) => ({ ...prev, open: false }))}
          />
        </div>
      ),
    });
  };

  const verifyApiUrl = `${process.env.REACT_APP_website_api}v1/website/operator/agent/profile/id/verify`;
  const operatorId = getOperatorId();

  const handleReject = async ({ message, privateNote }) => {
    setOpenConfirmation(false);
    try {
      await Promise.all([
        axios.post(verifyApiUrl, {
          operator_id: operatorId,
          partner_agent_id: parseInt(partnerAgentId),
          agent_mobile: agentData.agentMobile?.replace(/\D/g, ""),
          verified_status: 3,
          rejected_id_pics: allRejected,
          comments: message,
        }),
        updateAgent({
          variables: {
            operatorId,
            partnerAgentId: parseInt(partnerAgentId),
            verifiedStatus: 3,
            comments: privateNote || null,
            rejectedIdPics: allRejected,
          },
        }),
        saveStatusHistory({
          variables: {
            agentStatusHistoryArgs: [
              {
                partnerAgentId: parseInt(partnerAgentId),
                agentStatus: null,
                verifiedStatus: 3,
                agentStatusComments: message || "N/A",
                comments: privateNote || "N/A",
              },
            ],
          },
        }),
      ]);
      showSuccess("Successfully rejected agent.");
    } catch (error) {
      showError("Failed to reject agent. Please try again.");
    }
  };

  const handleAccept = async ({ message, privateNote }) => {
    setOpenConfirmation(false);
    try {
      await Promise.all([
        axios.post(verifyApiUrl, {
          operator_id: operatorId,
          partner_agent_id: parseInt(partnerAgentId),
          agent_mobile: agentData.agentMobile?.replace(/\D/g, ""),
          verified_status: 1,
          rejected_id_pics: [],
          comments: message,
        }),
        updateAgent({
          variables: {
            operatorId,
            partnerAgentId: parseInt(partnerAgentId),
            verifiedStatus: 1,
            rejectedIdPics: [],
          },
        }),
        saveStatusHistory({
          variables: {
            agentStatusHistoryArgs: [
              {
                partnerAgentId: parseInt(partnerAgentId),
                agentStatus: null,
                verifiedStatus: 1,
                agentStatusComments: message || "N/A",
                comments: privateNote || "N/A",
              },
            ],
          },
        }),
      ]);
      showSuccess("Successfully accepted agent.");
    } catch (error) {
      showError("Failed to accept agent. Please try again.");
    }
  };

  const confirmationConfig = {
    reject: {
      variant: "reject",
      title: "Reject Agent Verification",
      subtitle: "The agent will be notified with your message.",
      messageLabel: "Reason for rejection",
      messagePlaceholder: "Explain why this agent is being rejected...",
      defaultFallbackMessage: buildRejectionFallbackMessage(allRejected),
      showPrivateNote: true,
      onConfirm: handleReject,
    },
    accept: {
      variant: "accept",
      title: "Accept Agent Verification",
      subtitle: "The agent will be notified with your message.",
      messageLabel: "Message (optional)",
      messagePlaceholder: "Add a note for the agent...",
      defaultFallbackMessage:
        "Congratulations! Your MBOX Courier registration is approved. You can now drop-off packages at the locker.",
      showPrivateNote: false,
      onConfirm: handleAccept,
    },
  };

  return (
    <>
      <GlobalDialog
        visible={open}
        onHide={onHide}
        closable={true}
        dialogHeader="ID Verification"
        dialogClassName="w-[750px] font-montserrat"
        footer={
          <div className="grid grid-cols-2 gap-3 pt-6">
            <GlobalButton
              label="REJECT"
              buttonClassName="w-full bg-white border border-red-400 text-red-500 hover:bg-red-50 font-semibold py-2"
              onClick={() => {
                setConfirmationType("reject");
                setOpenConfirmation(true);
              }}
            />
            <GlobalButton
              label="ACCEPT"
              disabled={allRejected.length > 0}
              buttonClassName="w-full bg-primary-primaryYellow text-secondary-purpleGray hover:bg-primary-primaryYellowDark font-semibold py-2 border-0"
              onClick={() => {
                setConfirmationType("accept");
                setOpenConfirmation(true);
              }}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 text-secondary-purpleGray font-montserrat">
          {/* Agent Details */}
          <div className="grid grid-cols-12 gap-y-2 text-sm">
            <div className="col-span-12 font-semibold text-base mb-1">
              Agent Details
            </div>
            {[
              {
                label: "Full Name",
                value: [
                  agentData.firstName,
                  agentData.middleName,
                  agentData.lastName,
                ]
                  .filter(Boolean)
                  .join(" "),
              },
              { label: "Mobile Number", value: agentData.agentMobile },
              { label: "Birthdate", value: agentData.birthDate },
              { label: "Employee ID", value: agentData.employeeId },
              // {
              //   label: "Submission Type",
              //   value: submissionType
              //     ? `Type ${submissionType} — ${(SUBMISSION_TYPE_PHOTO_INDICES[submissionType] ?? []).map((i) => PHOTO_INDEX_TO_LABEL[i]).join(", ")}`
              //     : null,
              // },
            ].map(({ label, value }) => (
              <div key={label} className="contents">
                <div className="col-span-3 text-gray-400">{label}:</div>
                <div className="col-span-9 font-medium">{value || "N/A"}</div>
              </div>
            ))}
          </div>

          <hr className="w-full py-2" />

          {/* Submitted IDs */}
          <div className="col-span-12 font-semibold text-base mb-1">
            Submitted IDs
          </div>
          <p className="text-xs text-gray-400 -mt-2">
            You can mark up to {MAX_REJECTED_PHOTOS} photo
            {MAX_REJECTED_PHOTOS > 1 ? "s" : ""} as rejected.
          </p>
          <p className="text-xs text-red-500 -mt-2">
            {rejectedPhotos.length} photo
            {rejectedPhotos.length !== 1 ? "s" : ""} marked as rejected.
          </p>

          {/* Rejectable Fields */}
          <div className="flex flex-col gap-2">
            <div className="font-semibold text-sm">Rejectable Fields</div>
            {FIELD_INDICES.map((idx) => {
              const isChecked = rejectedFields.includes(idx);
              const isDisabled = !isChecked && isMaxFieldsReached;
              return (
                <label
                  key={idx}
                  className={`flex items-center gap-2 text-sm select-none ${
                    isDisabled
                      ? "cursor-not-allowed opacity-40"
                      : "cursor-pointer"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleRejectedField(idx)}
                    disabled={isDisabled}
                    className="accent-red-500 w-3.5 h-3.5 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <span className={isChecked ? "text-red-500 font-medium" : ""}>
                    {PHOTO_INDEX_TO_LABEL[idx]}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {submittedIds.map((item) => {
              const backendIndex = altToBackendIndex[item.alt] ?? -1;
              const isRejected = rejectedPhotos.includes(backendIndex);

              return (
                <div
                  key={item.alt}
                  className={`flex flex-col gap-2 border rounded-lg p-3 shadow-sm transition-colors ${
                    isRejected
                      ? "border-red-400 bg-red-50"
                      : !isRejected && isMaxReached
                        ? "border-gray-100 opacity-60"
                        : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400 font-medium">
                      {PHOTO_INDEX_TO_LABEL[backendIndex] || item.alt}
                    </span>
                    <label
                      className={`flex items-center gap-1.5 text-xs cursor-pointer select-none ${
                        isRejected
                          ? "text-red-500"
                          : isMaxReached
                            ? "text-gray-300 cursor-not-allowed"
                            : "text-red-500"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isRejected}
                        onChange={() => toggleRejectedPhoto(backendIndex)}
                        disabled={!isRejected && isMaxReached}
                        className="accent-red-500 w-3.5 h-3.5 cursor-pointer disabled:cursor-not-allowed"
                      />
                      Reject
                    </label>
                  </div>
                  <Image
                    src={item.src}
                    alt={item.alt}
                    imageClassName="w-full h-[180px] object-cover rounded-lg cursor-pointer transition duration-300 filter brightness-100 hover:brightness-50"
                    preview
                    pt={{ previewContainer: { className: "w-[600px]" } }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </GlobalDialog>

      <ConfirmationDialog
        open={openConfirmation}
        onClose={() => setOpenConfirmation(false)}
        {...(confirmationConfig[confirmationType] ?? {})}
      />

      <Modal
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        open={validation.open}
        actions={validation.actions}
      />
    </>
  );
}
